/* ============================================================================
 * Semantic status colour palette — parity with ps-customer-frontend.
 *
 * Returns Tailwind className strings (light + dark variants) for status badges,
 * chips, and icon backgrounds. Import these helpers instead of hardcoding
 * status colours. See THEME.md › "Status Color Palette".
 * ========================================================================== */

export const STATUS_COLORS = {
  green: 'bg-[#ECFDF5] text-[#0D9668] dark:bg-[#0D2E22] dark:text-[#34D399]',
  amber: 'bg-[#FFFBEB] text-[#B45309] dark:bg-[#3B2A0D] dark:text-[#FBBF24]',
  yellow: 'bg-yellow-50 text-yellow-700 dark:bg-[#2D2200] dark:text-[#FDE047]',
  red: 'bg-[#FEF2F2] text-[#DC2626] dark:bg-[#3D0D0D] dark:text-[#F87171]',
  blue: 'bg-blue-50 text-blue-700 dark:bg-[#0D1E40] dark:text-[#60A5FA]',
  purple: 'bg-[#F3F0FF] text-[#7C3AED] dark:bg-[#2D1F4E] dark:text-[#A78BFA]',
  orange: 'bg-orange-50 text-orange-700 dark:bg-[#3B2000] dark:text-[#FB923C]',
  gray: 'bg-[#F3F4F6] text-muted-foreground dark:bg-[#1E2D45]',
  brand: 'bg-brand-bg-subtle text-primary',
};

const upper = (s) => String(s || '').toUpperCase();

/** Requirement / opportunity status → swatch */
export function requirementStatusColor(status) {
  switch (upper(status)) {
    case 'ACTIVE':
      return STATUS_COLORS.green;
    case 'REVIEW':
      return STATUS_COLORS.amber;
    case 'DRAFT':
    default:
      return STATUS_COLORS.gray;
  }
}

/** Requirement deadline priority → swatch */
export function requirementTimelineColor(priority) {
  switch (upper(priority)) {
    case 'ON_TRACK':
      return STATUS_COLORS.green;
    case 'UPCOMING':
      return STATUS_COLORS.brand;
    case 'OVERDUE':
      return STATUS_COLORS.red;
    default:
      return STATUS_COLORS.gray;
  }
}

/** Invoice status → swatch */
export function invoiceStatusColor(status) {
  switch (upper(status)) {
    case 'GENERATED':
      return STATUS_COLORS.yellow;
    case 'PAYMENT_AWAITED':
    case 'PARTIALLY_PAID':
      return STATUS_COLORS.amber;
    case 'MODIFIED':
      return STATUS_COLORS.blue;
    case 'PAID':
      return STATUS_COLORS.green;
    case 'OVERDUE':
    case 'CANCELLED':
    case 'CANCELED':
      return STATUS_COLORS.red;
    default:
      return STATUS_COLORS.gray;
  }
}

/** Proforma invoice status → swatch */
export function proformaStatusColor(status) {
  switch (upper(status)) {
    case 'DRAFT':
      return STATUS_COLORS.yellow;
    case 'GENERATED':
      return STATUS_COLORS.amber;
    case 'APPROVED':
      return STATUS_COLORS.green;
    case 'MODIFIED':
    case 'REVISED':
      return STATUS_COLORS.blue;
    case 'CANCELLED':
    case 'CANCELED':
      return STATUS_COLORS.red;
    default:
      return STATUS_COLORS.gray;
  }
}

/** Purchase order status → swatch */
export function purchaseOrderStatusColor(status) {
  switch (upper(status)) {
    case 'DRAFT':
    case 'SENT':
      return STATUS_COLORS.yellow;
    case 'PENDING':
    case 'PENDING_APPROVAL':
      return STATUS_COLORS.amber;
    case 'APPROVED':
    case 'PAID':
    case 'COMPLETED':
      return STATUS_COLORS.green;
    case 'OVERDUE':
    case 'CANCELLED':
    case 'CANCELED':
      return STATUS_COLORS.red;
    default:
      return STATUS_COLORS.gray;
  }
}

/** Credit note status → swatch */
export function creditNoteStatusColor(status) {
  switch (upper(status)) {
    case 'ISSUED':
    case 'APPLIED':
      return STATUS_COLORS.green;
    case 'CANCELLED':
    case 'CANCELED':
      return STATUS_COLORS.red;
    default:
      return STATUS_COLORS.gray;
  }
}

/** Icon / stat-chip backgrounds */
export const ICON_BG = {
  brand: 'bg-brand-bg-subtle text-primary',
  green: STATUS_COLORS.green,
  amber: STATUS_COLORS.amber,
  yellow: STATUS_COLORS.yellow,
  red: STATUS_COLORS.red,
  blue: STATUS_COLORS.blue,
  purple: STATUS_COLORS.purple,
  orange: STATUS_COLORS.orange,
  gray: STATUS_COLORS.gray,
};
